import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Search, MapPin, Zap, TrendingDown, TrendingUp, Filter, ExternalLink, Info, Calculator, Star, Award, Clock, DollarSign, ChevronDown, ChevronUp, FileText, CreditCard, Percent, CheckCircle } from 'lucide-react';
import { Municipality, ElectricityProvider } from '../types';
import GoogleAd from '../components/GoogleAd';
import SidebarAd from '../components/SidebarAd';

const ProviderComparison: React.FC = () => {
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState<Municipality | null>(null);
  const [yearlyConsumption, setYearlyConsumption] = useState<number>(16000);
  const [providers, setProviders] = useState<ElectricityProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [municipalitySearchTerm, setMunicipalitySearchTerm] = useState('');
  const [showMunicipalityDropdown, setShowMunicipalityDropdown] = useState(false);
  const [sortBy, setSortBy] = useState<'price' | 'name' | 'savings'>('price');
  const [filterType, setFilterType] = useState<'all' | 'spot' | 'fixed'>('spot');
  const [expandedProviders, setExpandedProviders] = useState<Set<number>>(new Set());
  const [showExistingContract, setShowExistingContract] = useState(false);
  const [existingContract, setExistingContract] = useState({
    monthlyFee: 0,
    addonPrice: 0,
    contractType: 'spot' as 'spot' | 'fixed',
    fixedPrice: 0
  });

  // Load all providers on component mount with default consumption
  useEffect(() => {
    fetchAllProviders();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.relative')) {
        setShowMunicipalityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load municipalities on component mount
  useEffect(() => {
    const fetchMunicipalities = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/electricity-providers?endpoint=municipalities`,
          {
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
          }
        );
        const data = await response.json();
        setMunicipalities(data);
      } catch (error) {
        console.error('Error fetching municipalities:', error);
      }
    };

    fetchMunicipalities();
  }, []);

  // Filter municipalities based on search term
  const filteredMunicipalities = municipalities.filter(municipality =>
    municipality.name.toLowerCase().includes(municipalitySearchTerm.toLowerCase()) ||
    municipality.areaCode.toLowerCase().includes(municipalitySearchTerm.toLowerCase())
  );

  // Fetch providers when municipality or consumption changes
  useEffect(() => {
    if (selectedMunicipality) {
      fetchProviders();
    } else {
      // If no municipality selected, show all providers
      fetchAllProviders();
    }
  }, [selectedMunicipality, yearlyConsumption]);

  // Handle municipality selection
  const handleMunicipalitySelect = (municipality: Municipality) => {
    setSelectedMunicipality(municipality);
    setMunicipalitySearchTerm(municipality.name);
    setShowMunicipalityDropdown(false);
  };

  // Handle municipality search input
  const handleMunicipalitySearchChange = (value: string) => {
    setMunicipalitySearchTerm(value);
    setShowMunicipalityDropdown(true);
    
    // If the input is cleared, clear the selection
    if (!value) {
      setSelectedMunicipality(null);
    }
  };

  const fetchProviders = async () => {
    if (!selectedMunicipality) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/electricity-providers?endpoint=providers&gridId=${selectedMunicipality.id}&yearlyConsumption=${yearlyConsumption}`,
        {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      );
      const data = await response.json();
      setProviders(data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProviders = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/electricity-providers?endpoint=all-providers&yearlyConsumption=${yearlyConsumption}`,
        {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      );
      const data = await response.json();
      setProviders(data);
    } catch (error) {
      console.error('Error fetching all providers:', error);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort providers
  const filteredProviders = providers
    .filter(provider => {
      // Only show providers applicable to all customers
      if (provider.applicableToCustomerType !== 'allCustomers') {
        return false;
      }
      
      // Only show Spotpris and Fastpris contract types
      const contractType = provider.type.toLowerCase();
      if (!contractType.includes('spot') && !contractType.includes('fixed')) {
        return false;
      }
      
      // Filter out campaign prices (they usually have specific keywords)
      const name = provider.name.toLowerCase();
      const isCampaign = name.includes('kampanje') || 
                        name.includes('tilbud') || 
                        name.includes('bonus') || 
                        name.includes('rabatt') ||
                        name.includes('student') ||
                        name.includes('ung') ||
                        name.includes('senior') ||
                        name.includes('medlem') ||
                        name.includes('spesial');
      
      if (isCampaign) {
        return false;
      }
      
      return true;
    })
    .filter(provider => {
      const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           provider.organization.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || 
                         (filterType === 'spot' && provider.type.includes('spot')) ||
                         (filterType === 'fixed' && !provider.type.includes('spot'));
      
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.currentPrice.finalPriceMonthly - b.currentPrice.finalPriceMonthly;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'savings':
          const avgPrice = providers.reduce((sum, p) => sum + p.currentPrice.finalPriceMonthly, 0) / providers.length;
          const savingsA = avgPrice - a.currentPrice.finalPriceMonthly;
          const savingsB = avgPrice - b.currentPrice.finalPriceMonthly;
          return savingsB - savingsA;
        default:
          return 0;
      }
    });

  const cheapestProvider = providers.length > 0 
    ? providers.reduce((min, p) => p.currentPrice.finalPriceMonthly < min.currentPrice.finalPriceMonthly ? p : min, providers[0])
    : null;

  const averagePrice = providers.length > 0
    ? providers.reduce((sum, p) => sum + p.currentPrice.finalPriceMonthly, 0) / providers.length
    : 0;

  const getProviderTypeLabel = (type: string) => {
    if (type.includes('spot')) return 'Spotpris';
    if (type.includes('fixed')) return 'Fastpris';
    return 'Annet';
  };

  const getProviderTypeColor = (type: string) => {
    if (type.includes('spot')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    if (type.includes('fixed')) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
  };

  const calculateExistingContractCost = () => {
    if (existingContract.contractType === 'fixed') {
      return (existingContract.fixedPrice / 100) * (yearlyConsumption / 12) + existingContract.monthlyFee;
    } else {
      // For spot contracts, use average spot price + addon
      const avgSpotPrice = providers.length > 0 
        ? providers.reduce((sum, p) => sum + p.currentPrice.spotPrice, 0) / providers.length 
        : 60; // Default fallback
      return ((avgSpotPrice + existingContract.addonPrice) / 100) * (yearlyConsumption / 12) + existingContract.monthlyFee;
    }
  };

  const existingContractMonthlyCost = showExistingContract ? calculateExistingContractCost() : 0;

  const toggleProviderDetails = (providerId: number) => {
    const newExpanded = new Set(expandedProviders);
    if (newExpanded.has(providerId)) {
      newExpanded.delete(providerId);
    } else {
      newExpanded.add(providerId);
    }
    setExpandedProviders(newExpanded);
  };

  const getSavingsColor = (savings: number) => {
    if (savings > 0) return 'text-green-600 dark:text-green-400';
    if (savings < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  return (
    <>
      <Helmet>
        <title>{`Sammenlign Strømleverandører ${new Date().getFullYear()} | Finn Billigste Strømavtale Norge`}</title>
        <meta name="description" content={`Sammenlign alle strømleverandører i Norge ${new Date().getFullYear()} ⚡ Finn billigste strømavtale i din kommune ✓ Oppdaterte priser fra ${providers.length || '100+'} leverandører ✓ Spotpris og fastpris ✓ Gratis sammenligning ✓ Se besparelse med en gang`} />
        <meta name="keywords" content={`sammenlign strømleverandører ${new Date().getFullYear()}, billigste strømavtale norge, strømleverandør sammenligning, strømpriser leverandører, bytt strømleverandør, strømavtale sammenligning, beste strømavtale, spotpris avtale, fastpris strøm, strømleverandør priser, kraftleverandør sammenligning`} />
        <meta name="news_keywords" content={`strømleverandører ${new Date().getFullYear()}, billigste strømavtale, sammenlign kraftleverandører, strømpriser`} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://stromkalkulator.no/sammenlign-leverandorer" />
        <link rel="alternate" hrefLang="no" href="https://stromkalkulator.no/sammenlign-leverandorer" />
        <link rel="alternate" hrefLang="x-default" href="https://stromkalkulator.no/sammenlign-leverandorer" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://stromkalkulator.no/sammenlign-leverandorer" />
        <meta property="og:title" content={`Sammenlign Strømleverandører ${new Date().getFullYear()} | Finn Billigste Strømavtale`} />
        <meta property="og:description" content="Sammenlign alle strømleverandører i Norge. Finn billigste strømavtale i din kommune med oppdaterte priser." />
        <meta property="og:site_name" content="Strømkalkulator.no" />
        <meta property="og:locale" content="nb_NO" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Sammenlign Strømleverandører ${new Date().getFullYear()}`} />
        <meta name="twitter:description" content="Finn billigste strømavtale i Norge. Sammenlign priser fra alle leverandører." />
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto relative">
          {/* Hero Section */}
          <div className="hidden md:block bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 md:p-12 mb-8 text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Sammenlign Strømleverandører i Norge {new Date().getFullYear()}
            </h1>
            <p className="text-lg md:text-xl text-blue-100 mb-6 max-w-3xl">
              Finn den billigste strømavtalen i din kommune. Sammenlign oppdaterte priser, vilkår og tjenester fra alle strømleverandører i Norge på ett sted.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center bg-blue-700/50 rounded-lg px-4 py-2">
                <Award className="w-5 h-5 mr-2" />
                <span>Oppdaterte priser</span>
              </div>
              <div className="flex items-center bg-blue-700/50 rounded-lg px-4 py-2">
                <Calculator className="w-5 h-5 mr-2" />
                <span>Beregn besparelser</span>
              </div>
              <div className="flex items-center bg-blue-700/50 rounded-lg px-4 py-2">
                <ExternalLink className="w-5 h-5 mr-2" />
                <span>Direkte til leverandør</span>
              </div>
            </div>
          </div>

          {/* Existing Contract Comparison */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-lg mr-3">
                  <Calculator className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Sammenlign med din nåværende avtale
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Se hvor mye du kan spare ved å bytte leverandør
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowExistingContract(!showExistingContract)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showExistingContract
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {showExistingContract ? 'Skjul' : 'Legg til din avtale'}
              </button>
            </div>

            {showExistingContract && (
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Avtaletype
                    </label>
                    <select
                      value={existingContract.contractType}
                      onChange={(e) => setExistingContract(prev => ({ 
                        ...prev, 
                        contractType: e.target.value as 'spot' | 'fixed' 
                      }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                    >
                      <option value="spot">Spotpris</option>
                      <option value="fixed">Fastpris</option>
                    </select>
                  </div>

                  {existingContract.contractType === 'fixed' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Fast pris (øre/kWh)
                      </label>
                      <input
                        type="number"
                        value={existingContract.fixedPrice}
                        onChange={(e) => setExistingContract(prev => ({ 
                          ...prev, 
                          fixedPrice: parseFloat(e.target.value) || 0 
                        }))}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                        placeholder="120"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Påslag (øre/kWh)
                      </label>
                      <input
                        type="number"
                        value={existingContract.addonPrice}
                        onChange={(e) => setExistingContract(prev => ({ 
                          ...prev, 
                          addonPrice: parseFloat(e.target.value) || 0 
                        }))}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                        placeholder="5"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Månedlig avgift (kr)
                    </label>
                    <input
                      type="number"
                      value={existingContract.monthlyFee}
                      onChange={(e) => setExistingContract(prev => ({ 
                        ...prev, 
                        monthlyFee: parseFloat(e.target.value) || 0 
                      }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                      placeholder="29"
                    />
                  </div>

                  <div className="flex items-end">
                    <div className="w-full bg-orange-100 dark:bg-orange-900/30 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                      <div className="text-sm text-orange-700 dark:text-orange-300 mb-1">Din nåværende kostnad</div>
                      <div className="text-xl font-bold text-orange-800 dark:text-orange-200">
                        {existingContractMonthlyCost.toFixed(0)} kr/mnd
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400">
                  💡 Finn denne informasjonen på din siste strømregning eller i din leverandørs app/nettside
                </div>
              </div>
            )}
          </div>

          {/* Google Ad */}
          <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <GoogleAd
              slot="3068465121"
              format="fluid"
              layout="in-article"
              style={{
                display: 'block',
                margin: '0 auto',
                textAlign: 'center',
                minHeight: '100px'
              }}
            />
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 mb-6 mt-4 md:mt-0">
            {/* Mobile-only page title */}
            <div className="md:hidden mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Sammenlign leverandører
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Finn den billigste strømavtalen i din kommune
              </p>
            </div>
            
            <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6">
              {/* Municipality Selection */}
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Velg kommune
                </label>
                <div className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      value={municipalitySearchTerm}
                      onChange={(e) => handleMunicipalitySearchChange(e.target.value)}
                      onFocus={() => setShowMunicipalityDropdown(true)}
                      placeholder="Søk etter kommune..."
                      className="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm md:text-base pr-10"
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  
                  {/* Dropdown */}
                  {showMunicipalityDropdown && municipalitySearchTerm && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredMunicipalities.length > 0 ? (
                        filteredMunicipalities.slice(0, 10).map((municipality) => (
                          <button
                            key={municipality.id}
                            onClick={() => handleMunicipalitySelect(municipality)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-b-0 text-sm md:text-base"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {municipality.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {municipality.areaCode}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          Ingen kommuner funnet
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Yearly Consumption and Search combined on mobile */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-1 md:gap-0 md:col-span-2 lg:col-span-2 lg:grid-cols-2 lg:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Zap className="w-4 h-4 inline mr-1" />
                    <span className="hidden sm:inline">Årlig forbruk (kWh)</span>
                    <span className="sm:hidden">Forbruk</span>
                  </label>
                  <input
                    type="number"
                    value={yearlyConsumption}
                    onChange={(e) => setYearlyConsumption(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm md:text-base"
                    placeholder="16000"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden md:block">
                    Gjennomsnitt: Leilighet ~10000, Enebolig ~20000
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Search className="w-4 h-4 inline mr-1" />
                    <span className="hidden sm:inline">Søk leverandør</span>
                    <span className="sm:hidden">Søk</span>
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm md:text-base"
                    placeholder="Søk..."
                  />
                </div>
              </div>

              {/* Filter Type */}
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Filter className="w-4 h-4 inline mr-1" />
                  Avtaletype
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as 'all' | 'spot' | 'fixed')}
                  className="w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm md:text-base"
                >
                  <option value="all">Alle avtaler</option>
                  <option value="spot">Kun spotpris</option>
                  <option value="fixed">Kun fastpris</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Summary */}

          {/* Sort Options */}
          {providers.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                {filteredProviders.length} leverandører funnet
              </h2>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">Sorter etter:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'price' | 'name' | 'savings')}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="price">Laveste pris</option>
                  <option value="savings">Størst besparelse</option>
                  <option value="name">Navn A-Å</option>
                </select>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Henter leverandører...</span>
            </div>
          )}

          {/* No Municipality Selected */}
          {!selectedMunicipality && !loading && providers.length === 0 && (
            <div className="text-center py-8 md:py-12">
              <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Laster strømleverandører...
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Viser alle tilgjengelige strømleverandører med {yearlyConsumption.toLocaleString()} kWh årlig forbruk
              </p>
            </div>
          )}

          {/* Show all providers when no municipality selected */}
          {!selectedMunicipality && !loading && providers.length > 0 && (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                    Viser alle leverandører med {yearlyConsumption.toLocaleString()} kWh årlig forbruk
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Søk etter din kommune for mer nøyaktige priser og tilgjengelige avtaler
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Provider Results */}
          {!loading && filteredProviders.length > 0 && (
            <div className="space-y-3 md:space-y-4">
              {filteredProviders.map((provider, index) => {
                const savings = averagePrice - provider.currentPrice.finalPriceMonthly;
                const isRecommended = index === 0 && sortBy === 'price';
                const isExpanded = expandedProviders.has(provider.id);
                const savingsVsExisting = showExistingContract
                  ? existingContractMonthlyCost - provider.currentPrice.finalPriceMonthly
                  : 0;

                return (
                  <div
                    key={provider.id}
                    className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 md:p-6 transition-all hover:shadow-md ${
                      isRecommended
                        ? 'border-green-300 dark:border-green-700 ring-2 ring-green-100 dark:ring-green-900/30'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {isRecommended && (
                      <div className="flex items-center mb-3 md:mb-4">
                        <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                          <Award className="w-4 h-4 mr-1" />
                          Anbefalt - Billigst
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 mb-3 md:mb-4">
                      {/* Provider Info */}
                      <div className="lg:col-span-2">
                        <div className="flex items-start justify-between mb-2 md:mb-3">
                          <div>
                            <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-1">
                              {provider.name}
                            </h3>
                            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                              {provider.organization.name}
                            </p>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getProviderTypeColor(provider.type)}`}>
                              {getProviderTypeLabel(provider.type)}
                            </span>
                            {provider.conditions.associations.length > 0 && (
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-xs">
                                {provider.conditions.associations[0].name}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Conditions */}
                        <div className="space-y-1 md:space-y-2 text-xs md:text-sm">
                          {provider.conditions.agreementTime && (
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <Clock className="w-4 h-4 mr-2" />
                              Bindingstid: {provider.conditions.agreementTime} {provider.conditions.agreementTimeUnit}
                            </div>
                          )}
                          {provider.conditions.otherConditions && (
                            <div className="flex items-start text-gray-600 dark:text-gray-400">
                              <Info className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-xs md:text-sm">{provider.conditions.otherConditions}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="text-center lg:text-left">
                        <div className="mb-2">
                          <span className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                            {provider.currentPrice.finalPriceMonthly.toFixed(0)}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 ml-1">kr/mnd</span>
                        </div>
                        {showExistingContract && (
                          <div className={`text-sm font-medium mb-2 ${getSavingsColor(savingsVsExisting)}`}>
                            {savingsVsExisting > 0 ? (
                              <span className="flex items-center justify-center lg:justify-start">
                                <TrendingDown className="w-4 h-4 mr-1" />
                                Spar {Math.abs(savingsVsExisting).toFixed(0)} kr/mnd
                              </span>
                            ) : savingsVsExisting < 0 ? (
                              <span className="flex items-center justify-center lg:justify-start">
                                <TrendingUp className="w-4 h-4 mr-1" />
                                {Math.abs(savingsVsExisting).toFixed(0)} kr/mnd dyrere
                              </span>
                            ) : (
                              <span className="text-gray-500">Samme pris</span>
                            )}
                          </div>
                        )}
                        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          <div>Påslag: {provider.currentPrice.finalAddonPrice.toFixed(2)} øre/kWh</div>
                          <div>Månedlig avgift: {provider.fee.finalMonthlyFee.toFixed(0)} kr</div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row lg:flex-col justify-center space-y-2 sm:space-y-0 sm:space-x-2 lg:space-x-0 lg:space-y-3">
                        {provider.organization.pricelistUrl && (
                          <a
                            href={provider.organization.pricelistUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs md:text-sm font-medium"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Se prisliste
                          </a>
                        )}
                        {provider.orderUrl && (
                          <a
                            href={provider.orderUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-3 md:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs md:text-sm font-medium"
                          >
                            Bestill avtale
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Collapsible Details Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 md:pt-4">
                      <button
                        onClick={() => toggleProviderDetails(provider.id)}
                        className="w-full flex items-center justify-between py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 md:px-3 transition-colors"
                      >
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-2 text-gray-500" />
                          <span className="text-sm md:text-base font-medium text-gray-900 dark:text-white">
                            {isExpanded ? 'Skjul detaljer' : 'Vis detaljer'}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="mt-3 md:mt-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 md:p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {/* Pricing Breakdown */}
                            <div className="space-y-4">
                              <h4 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white flex items-center">
                                <DollarSign className="w-4 h-4 mr-2 text-blue-500" />
                                Prissammensetning
                              </h4>
                              <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                                  <span className="text-gray-600 dark:text-gray-400">Spotpris (siste 10 dager)</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.currentPrice.spotPrice.toFixed(2)} øre/kWh
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                                  <span className="text-gray-600 dark:text-gray-400">Påslag til {provider.organization.name}</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.currentPrice.finalAddonPrice.toFixed(2)} øre/kWh
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                                  <span className="text-gray-600 dark:text-gray-400">Total spotpris + påslag</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {(provider.currentPrice.spotPrice + provider.currentPrice.finalAddonPrice).toFixed(2)} øre/kWh
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    Ditt ca. forbruk {(yearlyConsumption / 12 / 1000).toFixed(1)} MWh * {(provider.currentPrice.spotPrice + provider.currentPrice.finalAddonPrice).toFixed(2)} øre
                                  </span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.currentPrice.electricityMonthlyConsumptionPrice.toFixed(0)} kr
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                                  <span className="text-gray-600 dark:text-gray-400">Fast beløp til {provider.organization.name} per måned</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.fee.finalMonthlyFee.toFixed(0)} kr
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg px-3 border-2 border-blue-200 dark:border-blue-700">
                                  <span className="font-semibold text-blue-900 dark:text-blue-200">Beregnet strømutgift for {format(new Date(), 'MMMM', { locale: nb })}</span>
                                  <span className="font-bold text-blue-900 dark:text-blue-200 text-lg">
                                    {provider.currentPrice.finalPriceMonthly.toFixed(0)} kr
                                  </span>
                                </div>
                                {showExistingContract && (
                                  <div className="flex justify-between items-center py-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg px-3 border-2 border-orange-200 dark:border-orange-700">
                                    <span className="font-semibold text-orange-900 dark:text-orange-200">
                                      {savingsVsExisting > 0 ? 'Månedlig besparelse' : savingsVsExisting < 0 ? 'Månedlig merkostnad' : 'Ingen forskjell'}
                                    </span>
                                    <span className={`font-bold text-lg ${getSavingsColor(savingsVsExisting)}`}>
                                      {savingsVsExisting > 0 ? '+' : ''}{savingsVsExisting.toFixed(0)} kr
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Contract Details */}
                            <div className="space-y-4">
                              <h4 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white flex items-center">
                                <FileText className="w-4 h-4 mr-2 text-green-500" />
                                Avtalevilkår
                              </h4>
                              <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Avtaletype:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {getProviderTypeLabel(provider.type)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Bindingstid:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.conditions.agreementTime 
                                      ? `${provider.conditions.agreementTime} ${provider.conditions.agreementTimeUnit}` 
                                      : 'Ingen bindingstid'
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Maks forbruk per år:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.conditions.maxKwhPerYear ? provider.conditions.maxKwhPerYear.toLocaleString() : 'Ikke spesifisert'} kWh
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Faktureringsfrekvens:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.billing.frequency} {provider.billing.unit === 'month' ? 'måned' : provider.billing.unit}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Betalingstype:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.paymentType === 'after' ? 'Etterskudd' : 'Forskudd'}
                                  </span>
                                </div>
                                {provider.conditions.otherConditions && (
                                  <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                    <p className="text-xs text-yellow-800 dark:text-yellow-200">
                                      <strong>Spesielle vilkår:</strong> {provider.conditions.otherConditions}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Fees and Additional Costs */}
                            <div className="space-y-4">
                              <h4 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white flex items-center">
                                <CreditCard className="w-4 h-4 mr-2 text-purple-500" />
                                Avgifter og tillegg
                              </h4>
                              <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Månedlig avgift:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.fee.finalMonthlyFee.toFixed(0)} kr
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Papirfaktura:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.fee.finalPostalLetterFee.toFixed(0)} kr
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Kontraktbrudd:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.fee.contractBreachFee ? 'Gebyr påløper' : 'Ingen gebyr'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">MVA-fritak:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.currentPrice.tax.vatExemption ? 'Ja' : 'Nei'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Elsertifikat-fritak:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.currentPrice.tax.elCertificateExemption ? 'Ja' : 'Nei'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Forbrukeravgift:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {provider.currentPrice.tax.consumerElectricityTax.toFixed(4)} kr/kWh
                                  </span>
                                </div>
                              </div>
                              
                              <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-600 rounded-lg">
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                  Nettleien kommer i tillegg til dette beløpet. Det er i nettleien at strømstøtten trekkes fra.
                                </p>
                                {provider.organization.pricelistUrl && (
                                  <a
                                    href={provider.organization.pricelistUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-xs"
                                  >
                                    Les mer om strømstøtten
                                    <ExternalLink className="w-3 h-3 ml-1" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* No Results */}
          {!loading && filteredProviders.length === 0 && providers.length > 0 && (
            <div className="text-center py-8 md:py-12">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Ingen leverandører funnet
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Prøv å endre søkekriteriene eller filteret
              </p>
            </div>
          )}

          {/* Information Section */}
          <div className="mt-8 md:mt-12 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 md:p-8 border border-blue-100 dark:border-blue-800">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">
              Slik sammenligner du strømleverandører
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow-sm">
                <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 flex items-center justify-center rounded-lg mb-4">
                  <Calculator className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base md:text-lg font-semibold mb-2 text-gray-900 dark:text-white">Beregn totalkostnad</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Se på både påslag per kWh og månedlige avgifter for å få den reelle kostnaden.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow-sm">
                <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 flex items-center justify-center rounded-lg mb-4">
                  <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base md:text-lg font-semibold mb-2 text-gray-900 dark:text-white">Sjekk bindingstid</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Noen avtaler har bindingstid. Vurder om du ønsker fleksibilitet eller forutsigbarhet.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow-sm">
                <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 flex items-center justify-center rounded-lg mb-4">
                  <Info className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base md:text-lg font-semibold mb-2 text-gray-900 dark:text-white">Les vilkårene</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Sjekk spesielle vilkår som medlemskap, aldersgrenser eller andre krav.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ProviderComparison;